import { defineStore } from 'pinia';
import axios from 'axios';
import { isRequestAborted } from '../utils/requestUtils';

export type EntityType = 'unit' | 'hero' | 'spell' | 'equipment' | 'domain' | 'faction';

interface LocalizedName {
  id: number;
  tech_name: string;
  name: string;
  type: EntityType;
}

interface NamesCache {
  [key: string]: LocalizedName; // key: `${type}:${tech_name}` or `${type}:id:${id}`
}

interface LocalizedNamesState {
  cache: NamesCache;
  loading: Set<string>; // Set of keys being loaded
  locale: string;
}

export const useLocalizedNamesStore = defineStore('localizedNames', {
  state: (): LocalizedNamesState => ({
    cache: {},
    loading: new Set(),
    locale: document.querySelector('meta[name="current-locale"]')?.getAttribute('content') || 'ru',
  }),

  getters: {
    getName: (state) => (type: EntityType, techName?: string, id?: number): string | null => {
      if (!techName && !id) return null;

      const key = id
        ? `${type}:id:${id}`
        : `${type}:${techName}`;

      return state.cache[key]?.name || null;
    },

    getNames: (state) => (items: Array<{ type: EntityType; tech_name?: string; id?: number }>): Record<string, string> => {
      const result: Record<string, string> = {};

      items.forEach((item) => {
        if (!item.tech_name && !item.id) return;

        const key = item.id
          ? `${item.type}:id:${item.id}`
          : `${item.type}:${item.tech_name}`;

        const cached = state.cache[key];
        if (cached) {
          const itemKey = item.id ? `id:${item.id}` : item.tech_name!;
          result[itemKey] = cached.name;
        }
      });

      return result;
    },
  },

  actions: {
    isBlank(value?: string | null): boolean {
      return !value || value.trim().length === 0;
    },

    getItemKey(item: { type: EntityType; tech_name?: string; id?: number }): string | null {
      if (!item.tech_name && !item.id) return null;
      return item.id ? `${item.type}:id:${item.id}` : `${item.type}:${item.tech_name}`;
    },

    async loadNames(
      items: Array<{ type: EntityType; tech_name?: string; id?: number }>,
      forceLocale?: string
    ): Promise<void> {
      const locale = forceLocale || this.locale;

      // Filter out items that are already cached or being loaded, and items without identifiers
      const itemsToLoad = items.filter((item) => {
        if (!item.tech_name && !item.id) return false;

        const key = this.getItemKey(item);
        if (!key) return false;

        // Check if cached name matches current locale (if locale changed, reload)
        const cached = this.cache[key];
        if (cached && !this.isBlank(cached.name) && this.locale === locale) {
          return false;
        }

        return !this.loading.has(key);
      });

      if (itemsToLoad.length === 0) return;

      // Mark items as loading
      itemsToLoad.forEach((item) => {
        const key = this.getItemKey(item);
        if (!key) return;
        this.loading.add(key);
      });

      try {
        // Group by type for batch request
        const requestData: Record<EntityType, Array<{ tech_name?: string; id?: number }>> = {
          unit: [],
          hero: [],
          spell: [],
          equipment: [],
          domain: [],
          faction: [],
        };

        itemsToLoad.forEach((item) => {
          requestData[item.type].push({
            tech_name: item.tech_name,
            id: item.id,
          });
        });

        const response = await axios.post('/api/dict/v1/names', requestData, {
          headers: {
            'Accept-Language': locale,
          },
        });

        // Cache the results
        Object.entries(response.data).forEach(([type, names]: [string, any]) => {
          if (Array.isArray(names)) {
            names.forEach((nameData: LocalizedName) => {
              const key = nameData.id
                ? `${type}:id:${nameData.id}`
                : `${type}:${nameData.tech_name}`;

              this.cache[key] = {
                ...nameData,
                type: type as EntityType,
              };
            });
          }
        });
      } catch (error) {
        if (!isRequestAborted(error)) {
          console.error('Error loading localized names:', error);
        }
      } finally {
        // Remove from loading set
        itemsToLoad.forEach((item) => {
          const key = this.getItemKey(item);
          if (!key) return;
          this.loading.delete(key);
        });
      }
    },

    async ensureNamesLoaded(
      items: Array<{ type: EntityType; tech_name?: string; id?: number }>,
      locale?: string
    ): Promise<void> {
      // Check if locale changed
      const currentLocale = locale || this.locale;
      if (currentLocale !== this.locale) {
        this.locale = currentLocale;
        // Clear cache when locale changes
        this.cache = {};
      }

      // Load missing names
      await this.loadNames(items, currentLocale);

      // Fallback: if EN translation is missing for some items, try RU
      // (so we show Russian instead of empty/mismatched strings)
      if (currentLocale === 'en') {
        const missing = items.filter((item) => {
          const key = this.getItemKey(item);
          if (!key) return false;
          const cached = this.cache[key];
          return !cached || this.isBlank(cached.name);
        });

        if (missing.length > 0) {
          // Only fetch items that are still missing, to avoid overriding existing EN names.
          await this.loadNames(missing, 'ru');
        }
      }
    },

    clearCache(): void {
      this.cache = {};
    },

    setLocale(locale: string): void {
      if (locale !== this.locale) {
        this.locale = locale;
        this.clearCache();
      }
    },
  },
});
